import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Handshake, Tag, CaretDown, CaretUp } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { AdminShell, Field } from "@/components/AdminShell";

const WINDOWS = [["", "Lifetime"], ["1", "1 month"], ["3", "3 months"], ["6", "6 months"], ["12", "12 months"]];
const winLabel = (w) => (w ? `${w} mo` : "lifetime");

export default function AdminDistributors() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cats, setCats] = useState([]);
  const [catForm, setCatForm] = useState({ name: "", commission_pct: 10, commission_window_months: "" });
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [edits, setEdits] = useState({});

  const loadAll = () => {
    api.get("/admin/distributor-categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/admin/distributors").then((r) => setData(r.data)).catch((e) => toast.error(errDetail(e)));
    api.get("/admin/payouts").then((r) => setPayouts(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/admin/distributors/settings").then((r) => setS(r.data)).catch((e) => toast.error(errDetail(e)));
    loadAll();
  }, []);

  const saveSettings = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.put("/admin/distributors/settings", {
        cookie_validity_days: parseInt(s.cookie_validity_days), transaction_fee_pct: parseFloat(s.transaction_fee_pct),
        tax_pct: parseFloat(s.tax_pct), min_payout_usd: parseFloat(s.min_payout_usd), payout_interval_days: parseInt(s.payout_interval_days),
      });
      setS(d); toast.success("Distributor settings saved");
    } catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };

  const createCat = async () => {
    try {
      await api.post("/admin/distributor-categories", { name: catForm.name, commission_pct: parseFloat(catForm.commission_pct),
        commission_window_months: catForm.commission_window_months ? parseInt(catForm.commission_window_months) : null });
      toast.success("Category created"); setCatForm({ name: "", commission_pct: 10, commission_window_months: "" }); loadAll();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const saveDist = async (d) => {
    const e = edits[d.id] || {};
    try {
      const body = {};
      if (e.category_id !== undefined) body.category_id = e.category_id;
      if (e.custom_pct !== undefined) { if (e.custom_pct === "") body.clear_custom_pct = true; else body.custom_pct = parseFloat(e.custom_pct); }
      if (e.custom_window !== undefined) { if (e.custom_window === "") body.clear_custom_window = true; else body.custom_window_months = parseInt(e.custom_window); }
      await api.put(`/admin/distributors/${d.id}`, body);
      toast.success("Distributor updated"); setEdits((x) => ({ ...x, [d.id]: undefined })); loadAll();
    } catch (er) { toast.error(errDetail(er)); }
  };

  const setStatus = async (d, status) => {
    try { await api.put(`/admin/distributors/${d.id}`, { status }); toast.success(`Distributor ${status}`); loadAll(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const toggleExpand = async (d) => {
    if (expanded[d.id]) { setExpanded((x) => ({ ...x, [d.id]: null })); return; }
    try { const { data: det } = await api.get(`/admin/distributors/${d.id}/detail`); setExpanded((x) => ({ ...x, [d.id]: det })); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const resolvePayout = async (p, action) => {
    let reason = null;
    if (action === "rejected") reason = window.prompt("Reason for rejection (optional):") || "";
    try {
      await api.post(`/admin/payouts/${p.id}/resolve`, { action, reason });
      toast.success(action === "paid" ? `Payout of $${p.amount.toFixed(2)} marked as paid` : "Payout rejected"); loadAll();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const ed = (id) => edits[id] || {};
  const pendingPayouts = payouts.filter((p) => p.status === "requested");

  return (
    <AdminShell title="Distributors" subtitle="Promoters who sell the software for a commission. Categories, rates, windows and payouts.">
      <div data-testid="admin-distributors-page" className="space-y-6">
        <div className="border border-gray-200 rounded-md p-4">
          <p className="text-xs font-bold text-gray-500 mb-3 flex items-center gap-1.5"><Handshake size={14} /> GLOBAL DISTRIBUTOR SETTINGS</p>
          {!s ? <p className="text-sm text-gray-400">Loading…</p> : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Referral cookie validity (days)" testId="dist-set-cookie" type="number" value={s.cookie_validity_days} onChange={(v) => setS({ ...s, cookie_validity_days: v })} />
              <Field label="Payment transaction fee (%)" testId="dist-set-fee" type="number" value={s.transaction_fee_pct} onChange={(v) => setS({ ...s, transaction_fee_pct: v })} />
              <Field label="Taxes (%)" testId="dist-set-tax" type="number" value={s.tax_pct} onChange={(v) => setS({ ...s, tax_pct: v })} />
              <Field label="Minimum payout ($)" testId="dist-set-minpayout" type="number" value={s.min_payout_usd} onChange={(v) => setS({ ...s, min_payout_usd: v })} />
              <Field label="Payout interval (days)" testId="dist-set-interval" type="number" value={s.payout_interval_days} onChange={(v) => setS({ ...s, payout_interval_days: v })} />
              <div className="flex items-end">
                <button data-testid="dist-settings-save" onClick={saveSettings} disabled={busy}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-5 py-2 disabled:opacity-50 w-full">Save settings</button>
              </div>
            </div>
          )}
        </div>

        <div className="border border-gray-200 rounded-md p-4">
          <p className="text-xs font-bold text-gray-500 mb-3 flex items-center gap-1.5"><Tag size={14} /> CATEGORIES</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {cats.map((c) => (
              <span key={c.id} data-testid={`cat-chip-${c.name}`} className="text-xs border border-gray-300 rounded-full px-3 py-1.5 bg-gray-50">
                <b>{c.name}</b> · {c.commission_pct}% · {winLabel(c.commission_window_months)}{c.is_default ? " · default" : ""}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div><label className="block text-[11px] font-semibold text-gray-500 mb-1">Name</label>
              <input data-testid="cat-name-input" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} placeholder="Gold" className="border border-gray-300 rounded-md px-2.5 py-1.5 text-sm w-32" /></div>
            <div><label className="block text-[11px] font-semibold text-gray-500 mb-1">Commission %</label>
              <input data-testid="cat-pct-input" type="number" value={catForm.commission_pct} onChange={(e) => setCatForm({ ...catForm, commission_pct: e.target.value })} className="border border-gray-300 rounded-md px-2.5 py-1.5 text-sm w-24" /></div>
            <div><label className="block text-[11px] font-semibold text-gray-500 mb-1">Window per user</label>
              <select data-testid="cat-window-select" value={catForm.commission_window_months} onChange={(e) => setCatForm({ ...catForm, commission_window_months: e.target.value })} className="border border-gray-300 rounded-md px-2.5 py-1.5 text-sm bg-white">
                {WINDOWS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
            <button data-testid="cat-create-btn" onClick={createCat} disabled={!catForm.name.trim()} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-1.5 disabled:opacity-50">Add category</button>
          </div>
        </div>

        {pendingPayouts.length > 0 && (
          <div data-testid="payout-queue" className="border border-amber-300 bg-amber-50/50 rounded-md p-4">
            <p className="text-xs font-bold text-amber-700 mb-3">⏳ PAYOUT REQUESTS ({pendingPayouts.length})</p>
            {pendingPayouts.map((p) => (
              <div key={p.id} data-testid={`payout-row-${p.id}`} className="flex items-center gap-3 flex-wrap bg-white border border-amber-200 rounded-md px-3 py-2 mb-2 text-sm">
                <span className="font-semibold">{p.distributor_name}</span>
                <span className="text-gray-500 text-xs">{p.distributor_email}</span>
                <span className="font-bold text-amber-800">${p.amount.toFixed(2)}</span>
                <span className="text-xs text-gray-400">requested {p.requested_at?.slice(0, 10)}</span>
                <span className="ml-auto flex gap-2">
                  <button data-testid={`payout-paid-${p.id}`} onClick={() => resolvePayout(p, "paid")} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md px-3 py-1.5">Mark paid</button>
                  <button data-testid={`payout-reject-${p.id}`} onClick={() => resolvePayout(p, "rejected")} className="border border-gray-300 text-gray-600 hover:bg-gray-50 text-xs font-semibold rounded-md px-3 py-1.5">Reject</button>
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="flex items-center px-4 pt-3 pb-1">
            <p className="text-xs font-bold text-gray-500">ALL DISTRIBUTORS ({data?.totals?.count ?? 0})</p>
            <span data-testid="adm-total-owed" className="ml-auto text-xs font-semibold bg-blue-50 text-blue-700 rounded-full px-3 py-1">Owed to distributors: ${(data?.totals?.owed ?? 0).toFixed(2)}</span>
          </div>
          <table className="w-full text-sm" data-testid="adm-dist-table">
            <thead className="bg-gray-50 text-left text-[11px] text-gray-400 uppercase">
              <tr><th className="px-4 py-2">Distributor</th><th className="px-2 py-2">Category</th><th className="px-2 py-2">Custom %</th><th className="px-2 py-2">Custom window</th>
                <th className="px-2 py-2">Effective</th><th className="px-2 py-2">Users</th><th className="px-2 py-2">Earned</th><th className="px-2 py-2">Balance</th><th className="px-2 py-2">Status</th><th className="px-2 py-2"></th></tr>
            </thead>
            <tbody>
              {(data?.distributors || []).map((d) => (
                <React.Fragment key={d.id}>
                <tr data-testid={`adm-dist-row-${d.email}`} className="border-t border-gray-100">
                  <td className="px-4 py-2.5">
                    <button data-testid={`adm-dist-expand-${d.id}`} onClick={() => toggleExpand(d)} className="flex items-center gap-1.5 hover:text-blue-600 text-left">
                      {expanded[d.id] ? <CaretUp size={12} /> : <CaretDown size={12} />}
                      <span><b>{d.name}</b><span className="block text-xs text-gray-400">{d.email} · /d/{d.ref_code}</span></span>
                    </button>
                  </td>
                  <td className="px-2 py-2.5">
                    <select data-testid={`adm-dist-cat-${d.id}`} value={ed(d.id).category_id ?? d.category_id ?? ""} onChange={(e) => setEdits((x) => ({ ...x, [d.id]: { ...ed(d.id), category_id: e.target.value } }))}
                      className="border border-gray-200 rounded px-1.5 py-1 text-xs bg-white max-w-[110px]">
                      {cats.map((c) => <option key={c.id} value={c.id}>{`${c.name} ${c.commission_pct}%`}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-2.5"><input data-testid={`adm-dist-pct-${d.id}`} type="number" placeholder="—" value={ed(d.id).custom_pct ?? d.custom_pct ?? ""} onChange={(e) => setEdits((x) => ({ ...x, [d.id]: { ...ed(d.id), custom_pct: e.target.value } }))} className="border border-gray-200 rounded px-1.5 py-1 text-xs w-16" /></td>
                  <td className="px-2 py-2.5">
                    <select data-testid={`adm-dist-window-${d.id}`} value={ed(d.id).custom_window ?? (d.custom_window_months ?? "")} onChange={(e) => setEdits((x) => ({ ...x, [d.id]: { ...ed(d.id), custom_window: e.target.value } }))} className="border border-gray-200 rounded px-1.5 py-1 text-xs bg-white">
                      <option value="">inherit</option>{WINDOWS.slice(1).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-2.5 text-xs text-gray-600" data-testid={`adm-dist-effective-${d.id}`}><b>{d.effective_pct}%</b> · {winLabel(d.effective_window)}</td>
                  <td className="px-2 py-2.5">{d.referred_count}</td>
                  <td className="px-2 py-2.5">${d.lifetime_earned.toFixed(2)}</td>
                  <td className="px-2 py-2.5 font-semibold" data-testid={`adm-dist-balance-${d.id}`}>${d.balance.toFixed(2)}{d.pending_payout > 0 && <span className="block text-[10px] text-amber-600">⏳ ${d.pending_payout.toFixed(2)}</span>}</td>
                  <td className="px-2 py-2.5">
                    {d.status === "active"
                      ? <button data-testid={`adm-dist-suspend-${d.id}`} onClick={() => setStatus(d, "suspended")} className="text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 hover:bg-red-100 hover:text-red-600" title="Click to suspend">active</button>
                      : <button data-testid={`adm-dist-activate-${d.id}`} onClick={() => setStatus(d, "active")} className="text-[10px] font-bold bg-red-100 text-red-600 rounded-full px-2 py-0.5 hover:bg-emerald-100 hover:text-emerald-700" title="Click to activate">{d.status}</button>}
                  </td>
                  <td className="px-2 py-2.5">{edits[d.id] && <button data-testid={`adm-dist-save-${d.id}`} onClick={() => saveDist(d)} className="text-xs text-blue-600 font-semibold hover:underline">Save</button>}</td>
                </tr>
                {expanded[d.id] && (
                  <tr data-testid={`adm-dist-detail-${d.id}`}><td colSpan={10} className="bg-gray-50/70 px-6 py-3 border-t border-gray-100">
                    <p className="text-[11px] font-bold text-gray-500 mb-1.5">REFERRED USERS ({expanded[d.id].referrals.length})</p>
                    {expanded[d.id].referrals.length === 0 ? <p className="text-xs text-gray-400 mb-2">None yet</p> : (
                      <table className="w-full text-xs mb-3"><thead className="text-left text-[10px] uppercase text-gray-400"><tr><th className="py-1 pr-3">Email</th><th className="py-1 pr-3">Registered</th><th className="py-1 pr-3">Window</th><th className="py-1 pr-3">Earning until</th><th className="py-1">Commission</th></tr></thead>
                        <tbody>{expanded[d.id].referrals.map((r, i) => (
                          <tr key={i} className="border-t border-gray-100 text-gray-700"><td className="py-1 pr-3">{r.email}</td><td className="py-1 pr-3">{r.attributed_at?.slice(0, 10)}</td>
                            <td className="py-1 pr-3">{r.window_months ? `${r.window_months} mo` : "lifetime"}</td><td className="py-1 pr-3">{r.commission_until?.slice(0, 10) || "∞"}</td><td className="py-1 text-emerald-700 font-semibold">${r.commission.toFixed(2)}</td></tr>))}
                        </tbody></table>)}
                    <p className="text-[11px] font-bold text-gray-500 mb-1.5">LEDGER (last {expanded[d.id].ledger.length})</p>
                    {expanded[d.id].ledger.length === 0 ? <p className="text-xs text-gray-400">Empty</p> : (
                      <div className="max-h-44 overflow-y-auto">
                        <table className="w-full text-xs"><tbody>{expanded[d.id].ledger.map((l) => (
                          <tr key={l.id} className="border-t border-gray-100 text-gray-600"><td className="py-1 pr-3">{l.created_at?.slice(0, 10)}</td><td className="py-1 pr-3 uppercase text-[10px] font-bold">{l.type}</td>
                            <td className="py-1 pr-3">{l.note || "—"}</td><td className={`py-1 font-semibold ${l.net_amount >= 0 ? "text-emerald-700" : "text-blue-700"}`}>{l.net_amount >= 0 ? "+" : "−"}${Math.abs(l.net_amount).toFixed(2)}</td></tr>))}
                        </tbody></table></div>)}
                  </td></tr>
                )}
                </React.Fragment>
              ))}
              {(data?.distributors || []).length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-gray-400">No distributors yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </AdminShell>
  );
}
